import Mediator from 'gisw/Classes/Mediator';

import sleep from 'src/utils/sleep';

import GWindow from '../GWindow';
import NodeHash from '../NodeHash';

import Resizable from './Resizable';

const REFRESH_DELAY_DURATION = 300;

/**
 * @typedef {object} GWindowListConfig
 * @property {string} id
 * @property {Array<import('../GWindow').GWindowConfig>} [initGWindows]
 * @property {boolean} [resizable]
 */

class GWindowList {
  domNode = document.createElement('div');

  /**
   * @param {GWindowListConfig} config
   */
  constructor (config) {
    this.domNode.classList.add('gisw-wndw-list');

    const { id, initGWindows = [], resizable = false } = config;

    this.domNode.id = id;

    /** @private @type {Map<string, GWindow>} */
    this.gWindows = new Map();

    /** @private */
    this.resizeHelper = new Resizable({
      domRef: this.domNode,
      startResizing: resizable
    });

    /**
     * @typedef {object} GWindowItem
     * @property {string} [id]
     * @property {string} key
     * @property {import('src/web-components/GWorksWindow').default} node
     */

    /**
     * @private
     * @type {NodeHash<GWindowItem>}
     */
    this.nodeHash = new NodeHash({
      id: id + 'NodeHash',
      className: 'gisw-wndw-list__hash',
      createNode: p => p.node,
      noNestedDiv: true
    });
    this.domNode.appendChild(this.nodeHash.domNode);

    this.add(initGWindows);

    this.windowResizeHandler = this.windowResizeHandler.bind(this);
    window.addEventListener('resize', this.windowResizeHandler);
  }

  destroy () {
    window.removeEventListener('resize', this.windowResizeHandler);
    this.resizeHelper.enableResizing(false);
    this.nodeHash.destroy();

    for (const gWindow of this.gWindows.values()) {
      gWindow.destroy();
    }

    this.domNode.remove();
  }

  /**
   * @private
   */
  windowResizeHandler () {
    this.delayRefresh(REFRESH_DELAY_DURATION);
  }

  autoResizeHandler () {
    this.delayRefresh(REFRESH_DELAY_DURATION);
  }

  /**
   * @param {Array<import('../GWindow').GWindowConfig>} params
   */
  add (params) {
    params.forEach(param => {
      this._trackGWin(_createGWindow(param), param.key);
    });
  }

  /**
   * @param {import('../GWindow').GWindowConfig} param
   */
  addTool (param) {
    this._trackTool(_createToolGWindow(param), param.key);
  }

  /**
   * @param {string} key
   * @param {object} [options]
   * @param {boolean} [options.preventDestruction]
   */
  remove (key, options = {}) {
    if (!this.isGWindowKey(key)) {
      return null;
    }

    const { preventDestruction = false } = options;

    const gWindow = this._untrackGWin(key);

    if (!preventDestruction && gWindow) {
      gWindow.destroy();
    }

    return gWindow;
  }

  /**
   * @param {string} key
   */
  deleteGWin (key) {
    const gwin = this.remove(key);

    if (gwin) {
      gwin.deleteGWin();
    }
  }

  /**
   * @param {string} key
   */
  isGWindowKey (key) {
    return this.nodeHash.containsKey(key);
  }

  /**
   * @param {string} key
   */
  getGWindow (key) {
    if (!this.isGWindowKey(key)) {
      return null;
    }

    return this.gWindows.get(key);
  }

  /**
   * @param {string} key
   * @param {HTMLElement} c
   */
  setContent (key, c) {
    if (!this.isGWindowKey(key)) {
      return;
    }

    this.gWindows.get(key).setContent(c);
    this.delayRefresh(300);
  }

  closeAllOpened () {
    return this.toggleKeys(
      new Set(
        Array.from(this.gWindows.entries())
          .filter(([, gWindow]) => !gWindow.isMinimized)
          .map(([key]) => key)
      )
    );
  }

  /**
   * @deprecated
   */
  openAllClosed () {
    return this.toggleKeys(
      new Set(
        Array.from(this.gWindows.entries())
          .filter(([, gWindow]) => gWindow.isMinimized)
          .map(([key]) => key)
      )
    );
  }

  /**
   * @private
   * @param {Set<string>} keySet
   */
  async toggleKeys (keySet) {
    const promises = Array.from(keySet)
      .reverse()
      .map(key => this.toggleKey(key));

    await Promise.all(promises);
  }

  /**
   * @param {string} key
   */
  async toggleKey (key) {
    if (!this.nodeHash.containsKey(key)) {
      return;
    }

    const gwin = this.gWindows.get(key);

    gwin.toggle();
    await this.refresh();
  }

  /**
   * @param {number} timeout
   */
  delayRefresh (timeout) {
    clearTimeout(this.__timeoutID);

    /** @private */
    this.__timeoutID = setTimeout(() => {
      if (this.gWindows.size > 0) {
        this.refresh();
      }

      this.__timeoutID = null;
    }, timeout);
  }

  async refresh () {
    // Waits for batched updates in `<gworks-window />` to complete
    await sleep();

    const visibleGWindows = Array.from(this.gWindows.values()).filter(
      gWindow => getComputedStyle(gWindow.domNode).display !== 'none'
    );

    const gWindowsOpened = visibleGWindows.filter(
      gWindow => !gWindow.isMinimized
    );

    const gWindowsClosed = visibleGWindows.filter(
      gWindow => gWindow.isMinimized
    );

    // Calculate new `--content-max-height` for each opened gWindow
    const totalSpace = Number.parseInt(
      getComputedStyle(this.nodeHash.domNode).maxHeight.replace('px', '')
    );
    const closedWindowSpace = gWindowsClosed.reduce(
      (total, gWindow) =>
        total + gWindow.domNode.getBoundingClientRect().height,
      0
    );
    const marginSpace = visibleGWindows
      .map(gWindow => {
        const { marginBottom } = getComputedStyle(gWindow.domNode);

        return Number.parseInt(marginBottom.replace('px', ''));
      })
      .reduce((total, current) => total + current, 0);

    const usableSpace = totalSpace - (closedWindowSpace + marginSpace);

    // Distribute the remaining free space
    const newMaxHeight = Math.floor(usableSpace / gWindowsOpened.length);

    await Promise.all(
      gWindowsOpened.map(gWindow => {
        gWindow.domNode.style.setProperty(
          '--content-max-height',
          `${newMaxHeight}px`
        );

        return gWindow.refresh();
      })
    );
  }

  /**
   * @private
   * @param {GWindow} gwin
   * @param {string} key
   */
  _trackGWin (gwin, key) {
    this.nodeHash.add([{ key, node: gwin.domNode }]);
    this.gWindows.set(key, gwin);
    gwin.domNode.addEventListener('toggle', () => this.refresh());

    // Would prefer to have this outside of GWindowList later
    if (key === 'layers') {
      // Syncfusion won't render correctly if the gWindow is shut
      // Dangit Syncfusion!
      gwin.domNode.addEventListener('toggle', async () => {
        await sleep();

        try {
          const tocNode = gwin.domNode.querySelector('.toc');

          tocNode
            .querySelector(`#${tocNode.id}--slider`)
            // @ts-ignore
            .ej2_instances[0].refresh();
        } catch (error) {
          // Shh
        }
      });
    }

    gwin.addCloseCallback(key => {
      this.remove(key);
      this.refresh();
    });
  }

  /**
   * @private
   * @param {GWindow} gwin
   * @param {string} key
   */
  _trackTool (gwin, key) {
    gwin.addCloseCallback(key => {
      this.remove(key);
      this.refresh();
    });

    this.nodeHash.add([{ key, id: gwin.id + 'Container', node: gwin.domNode }]);
    this.gWindows.set(key, gwin);

    gwin.domNode.addEventListener('toggle', () => this.refresh());

    const tempLayers = this.getGWindow('layers');
    const tempBasemap = this.getGWindow('basemaps');
    this.remove('layers');
    this.remove('basemaps');

    this.add([
      {
        content: tempLayers.content,
        id: 'layersContainer',
        key: 'layers',
        title: 'Layers'
      },
      {
        content: tempBasemap.content,
        id: 'basemapContainer',
        key: 'basemaps',
        title: 'Basemap'
      }
    ]);

    if (Mediator.isMobile && !Mediator.desktopMode) {
      document.body.appendChild(this.getGWindow('activeTool').domNode);
    }
  }

  /**
   * @private
   * @param {string} key
   */
  _untrackGWin (key) {
    const gwin = this.gWindows.get(key);

    this.nodeHash.remove([key]);

    this.gWindows.delete(key);

    return gwin;
  }
}

export default GWindowList;

/**
 * @param {import('../GWindow').GWindowConfig} param
 */
function _createGWindow (param) {
  return new GWindow({ startMinimized: true, ...param });
}

/**
 * @param {import('../GWindow').GWindowConfig} param
 */
function _createToolGWindow (param) {
  return new GWindow({
    autoResize: true,
    'class': 'toolGWindow',
    showCloseButton: true,
    ...param
  });
}
